import React, { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Locate, MapPin, Loader2, Search, X } from "lucide-react";
import { addProviderTileLayer } from "@/lib/mapProvider";

/**
 * LocationPicker — self-contained component for picking a delivery location.
 *
 * Features:
 *   1. "Use current location" button (browser Geolocation API + reverse-geocode to address)
 *   2. Interactive Leaflet map — click / drag pin to set exact spot
 *   3. Search bar (OSM Nominatim forward-geocode) with autocomplete
 *   4. Editable full-address text box
 *
 * Emits `onChange({ lat, lng, address })` whenever any of them change.
 * All map/geocoding traffic is direct browser → OSM (no server dependency).
 */
export default function LocationPicker({ value, onChange, defaultCenter = [18.5204, 73.8567] }) {
  const mapEl = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);

  const [address, setAddress] = useState(value?.address || "");
  const [lat, setLat] = useState(value?.lat || null);
  const [lng, setLng] = useState(value?.lng || null);
  const [locating, setLocating] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  // Emit changes upward via a ref to avoid useEffect dep loops
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => {
    onChangeRef.current?.({ lat, lng, address });
  }, [lat, lng, address]);

  // Sync from external `value` prop (e.g. site changed in parent → prefill new coords/address).
  // Deps are limited to the incoming value; comparing against internal state prevents
  // acting on our own emitted updates. Note: intentionally not depending on internal
  // lat/lng/address to avoid an update loop (parent re-emits → parent value === state).
  const lastSynced = useRef({ lat: null, lng: null, address: "" });
  useEffect(() => {
    if (!value) return;
    const nextLat = value.lat ?? null;
    const nextLng = value.lng ?? null;
    const nextAddr = value.address ?? "";
    // Skip if this is the same value we just synced OR the same as what we last emitted.
    if (
      nextLat === lastSynced.current.lat &&
      nextLng === lastSynced.current.lng &&
      nextAddr === lastSynced.current.address
    ) return;
    lastSynced.current = { lat: nextLat, lng: nextLng, address: nextAddr };
    if (nextAddr) setAddress(nextAddr);
    if (nextLat && nextLng) {
      setLat(nextLat);
      setLng(nextLng);
      if (marker.current && map.current) {
        marker.current.setLatLng([nextLat, nextLng]);
        map.current.setView([nextLat, nextLng], map.current.getZoom());
      }
    }
  }, [value?.lat, value?.lng, value?.address]); // eslint-disable-line react-hooks/exhaustive-deps -- narrower deps (individual fields) are intentional; syncing on whole `value` object identity would fire extra times.

  const reverseGeocode = useCallback(async (la, ln) => {
    try {
      const r = await api.get("/geo/reverse", { params: { lat: la, lng: ln } });
      const display = r.data?.display_name || "";
      if (display) setAddress(display);
    } catch {
      /* upstream may throttle — user can type address manually */
    }
  }, []);

  const setPin = useCallback((la, ln, opts = {}) => {
    setLat(la);
    setLng(ln);
    if (marker.current && map.current) {
      marker.current.setLatLng([la, ln]);
      map.current.setView([la, ln], opts.zoom || map.current.getZoom());
    }
    if (opts.reverseGeocode !== false) {
      reverseGeocode(la, ln);
    }
  }, [reverseGeocode]);

  // Init Leaflet map once
  useEffect(() => {
    if (map.current || !mapEl.current) return;
    const initLat = lat || defaultCenter[0];
    const initLng = lng || defaultCenter[1];
    const m = L.map(mapEl.current).setView([initLat, initLng], 15);
    addProviderTileLayer(L, m);
    const icon = L.divIcon({
      className: "location-pin-marker",
      html: `<div style="width:32px;height:32px;background:#F26722;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid white;box-shadow:0 4px 8px rgba(0,0,0,.3);"><div style="width:10px;height:10px;background:white;border-radius:50%;margin:8px auto;transform:rotate(45deg);"></div></div>`,
      iconSize: [32, 32], iconAnchor: [16, 32],
    });
    const mk = L.marker([initLat, initLng], { draggable: true, icon }).addTo(m);
    mk.on("dragend", (e) => {
      const p = e.target.getLatLng();
      setPin(p.lat, p.lng);
    });
    m.on("click", (e) => setPin(e.latlng.lat, e.latlng.lng));
    map.current = m;
    marker.current = mk;
    // Init-once effect. `lat`/`lng`/`defaultCenter`/`setPin` are seed values
    // for the initial map view; re-running would recreate the map on every
    // parent update. Marker + pan updates happen in the sync effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by this browser");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        setPin(pos.coords.latitude, pos.coords.longitude, { zoom: 17 });
        toast.success("Pin set to your current location");
      },
      (err) => {
        setLocating(false);
        toast.error(err.code === 1 ? "Location permission denied — allow it and retry" : "Could not read location");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const runSearch = useCallback(async (q) => {
    if (!q.trim() || q.trim().length < 3) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const r = await api.get("/geo/search", { params: { q: q.trim(), country: "in", limit: 5 } });
      setSearchResults(r.data?.results || []);
    } catch (e) {
      setSearchResults([]);
      toast.error(e?.response?.data?.detail || "Address search failed. Try again in a moment.");
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced auto-search as the user types — 500 ms of quiet keyboard time
  // triggers a lookup. Also skips lookups shorter than 3 characters (avoids
  // hammering the geocoder for very short queries).
  useEffect(() => {
    const q = searchQ.trim();
    if (q.length < 3) { setSearchResults([]); return; }
    const t = setTimeout(() => { runSearch(q); }, 500);
    return () => clearTimeout(t);
  }, [searchQ, runSearch]);

  return (
    <div className="space-y-3">
      {/* Search + geolocate */}
      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search for an address, landmark, city..."
            value={searchQ}
            onChange={(e) => { setSearchQ(e.target.value); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(searchQ); } }}
            className="h-10 pl-8 pr-8"
            data-testid="location-search-input"
          />
          {searchQ && (
            <button
              type="button"
              onClick={() => { setSearchQ(""); setSearchResults([]); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={useMyLocation}
          disabled={locating}
          data-testid="btn-use-my-location"
          className="h-10"
        >
          {locating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Locate className="mr-2 h-4 w-4" />}
          Use my location
        </Button>
      </div>

      {searching && <div className="text-xs text-muted-foreground">Searching…</div>}
      {searchResults.length > 0 && (
        <div className="rounded-md border bg-background shadow-sm" data-testid="location-search-results">
          {searchResults.map((r) => (
            <button
              key={`${r.place_id}`}
              type="button"
              onClick={() => {
                setPin(parseFloat(r.lat), parseFloat(r.lon), { zoom: 17, reverseGeocode: false });
                setAddress(r.display_name);
                setSearchResults([]);
                setSearchQ("");
              }}
              className="flex w-full items-start gap-2 border-b p-2.5 text-left text-xs last:border-0 hover:bg-muted/50"
            >
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="line-clamp-2">{r.display_name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Map */}
      <div
        ref={mapEl}
        className="location-picker-map h-64 w-full overflow-hidden rounded-lg border sm:h-72"
        style={{ isolation: "isolate", position: "relative", zIndex: 0 }}
        data-testid="location-picker-map"
      />

      {/* Address text */}
      <div>
        <Label className="text-xs">Delivery address (edit as needed)</Label>
        <textarea
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          rows={2}
          placeholder="Building name, street, landmark, area, city, PIN"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          data-testid="location-address-input"
        />
        {(lat && lng) ? (
          <div className="mt-1 text-[11px] text-muted-foreground">
            📍 <code className="font-mono">{lat.toFixed(6)}, {lng.toFixed(6)}</code>
          </div>
        ) : (
          <div className="mt-1 text-[11px] text-orange-600">Set a pin using search, &quot;Use my location&quot;, or by clicking the map.</div>
        )}
      </div>
    </div>
  );
}
